/**
 * SEO HEADING STRUCTURE - Quizzes
 * ================================
 * 
 * TITLE: Quizzes - AI-Powered Practice Tests | CSW | Online Workspace...
 * META DESCRIPTION: Create custom quizzes for studying. AI-powered question generation...
 * 
 * H1 (1 tag):
 *   - "Quizzes" (hero section)
 * 
 * H2 (6+ tags):
 *   - "Quiz Features" (features section)
 *   - "Compare Quiz Platforms" (comparison section)
 *   - "Who Uses CSW Quizzes?" (use cases section)
 *   - "Quiz Tips & Strategies" (tips section)
 *   - "Ready to Test Your Knowledge?" (CTA section)
 * 
 * H3 (15+ tags):
 *   - Feature card titles (4): "Custom Quizzes", "Instant Feedback", etc.
 *   - Comparison platform names (15 links to comparison pages)
 *   - Use case titles: "Students", "Professionals", "Content Creators"
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Brain, Zap, TrendingUp, Trophy, ArrowRight, ExternalLink, Sparkles, Palette, Users, CheckCircle, Presentation, Heart, GraduationCap, Briefcase, PartyPopper } from "lucide-react";
import { Link } from "react-router-dom";

const features = [
  {
    icon: Brain,
    title: "Custom Quizzes",
    description: "Create your own quizzes or let AI generate questions from your notes",
  },
  {
    icon: Zap,
    title: "Instant Feedback",
    description: "Get immediate results and explanations for every answer",
  },
  {
    icon: TrendingUp,
    title: "Progress Tracking",
    description: "Monitor your improvement across topics and subjects",
  },
  {
    icon: Trophy,
    title: "Adaptive Learning",
    description: "Focus on areas where you need more practice",
  },
];

const comparisonPages = [
  { name: "Quizlet", path: "/quizzes/csw-vs-quizlet/", description: "Flashcards & practice tests" },
  { name: "Test Prep Review", path: "/quizzes/csw-vs-test-prep-review/", description: "SAT, ACT, GRE practice" },
  { name: "Studiosity", path: "/quizzes/csw-vs-studiosity/", description: "Quizzes & worksheets" },
  { name: "Thea Study", path: "/quizzes/csw-vs-thea/", description: "Test-condition practice" },
  { name: "Quiz Me", path: "/quizzes/csw-vs-quiz-me/", description: "Text to quiz generator" },
  { name: "FlexiQuiz", path: "/quizzes/csw-vs-flexiquiz/", description: "Online quiz maker" },
  { name: "Quiz Maker", path: "/quizzes/csw-vs-quiz-maker/", description: "Simple quiz creator" },
  { name: "Revisely", path: "/quizzes/csw-vs-revisely/", description: "AI quiz generator" },
  { name: "QuizWhiz", path: "/quizzes/csw-vs-quizwhiz/", description: "PDF to quiz converter" },
  { name: "Testudy", path: "/quizzes/csw-vs-testudy/", description: "Text to flashcard/quiz" },
  { name: "Canva Quiz Maker", path: "/quizzes/csw-vs-canva/", description: "Visual quiz design" },
  { name: "Google Forms", path: "/quizzes/csw-vs-google-forms/", description: "Auto-grading quizzes" },
  { name: "ClassMarker", path: "/quizzes/csw-vs-classmarker/", description: "Professional quiz analytics" },
  { name: "Wooclap", path: "/quizzes/csw-vs-wooclap/", description: "Interactive engagement" },
  { name: "Genially", path: "/quizzes/csw-vs-genially/", description: "Dynamic quiz templates" },
];

const quizTopicPages = [
  { name: "Fun Quizzes to Take", path: "/quizzes/fun-quizzes-to-take/", description: "Best quizzes when bored" },
  { name: "Video Game Quizzes", path: "/quizzes/video-game-quizzes/", description: "Test your gaming knowledge" },
  { name: "Art Quizzes", path: "/quizzes/art-quizzes/", description: "Famous paintings & artists" },
  { name: "Music Quizzes", path: "/quizzes/music-quizzes/", description: "Songs, bands & genres" },
  { name: "Food Quizzes", path: "/quizzes/food-quizzes/", description: "Culinary trivia & cuisine" },
  { name: "History Quizzes", path: "/quizzes/history-quizzes/", description: "Historical events & figures" },
  { name: "Science Quizzes", path: "/quizzes/science-quizzes/", description: "Scientific knowledge tests" },
  { name: "Pop Culture Quizzes", path: "/quizzes/pop-culture-quizzes/", description: "Movies, TV & trends" },
  { name: "Trivia Quizzes", path: "/quizzes/trivia-quizzes/", description: "General knowledge tests" },
  { name: "Personality Quizzes", path: "/quizzes/personality-quizzes/", description: "Discover yourself" },
];

const Quizzes = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Quizzes - AI-Powered Practice Tests"
        description="Create custom quizzes for studying. AI-powered question generation, instant feedback, progress tracking, and adaptive learning for better results."
        keywords="online quizzes, quiz maker, study quizzes, practice tests, quiz generator"
        canonicalUrl="/quizzes/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Test Your Knowledge</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Quizzes</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  Test your knowledge and track your learning progress with custom and AI-generated quizzes.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Start Quiz
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Create Quiz
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">AI</p>
                    <p className="text-sm text-muted-foreground">Generated</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Instant</p>
                    <p className="text-sm text-muted-foreground">Feedback</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Your Progress</p>
                      <p className="text-xl font-bold text-foreground">85% accuracy</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Brain className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Smart{" "}
                <span className="text-gradient">Learning</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Powerful quiz features to accelerate your learning
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl text-center"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                How It{" "}
                <span className="text-gradient">Works</span>
              </h2>
            </div>
            
            <div className="space-y-8">
              {[
                { step: 1, title: "Choose Your Topic", desc: "Select from existing quizzes or create your own with custom questions" },
                { step: 2, title: "Take the Quiz", desc: "Answer questions at your own pace with clear, intuitive interface" },
                { step: 3, title: "Review & Learn", desc: "See detailed results, correct answers, and areas for improvement" },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex gap-6 items-start p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg group"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary text-primary-foreground flex items-center justify-center font-bold text-xl font-display group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 font-display">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-muted" />
          <div className="absolute top-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm mb-6">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Transform Learning</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Benefits of Using{" "}
                <span className="text-gradient">Quizzes</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Quizzes turn lessons into lively, interactive experiences that keep participants involved from start to finish. With instant feedback and dynamic question formats, learners stay motivated and deeply engaged.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 group">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Zap className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold font-display">Interactive Experiences</h3>
                  <p className="text-muted-foreground">Keep participants involved from start to finish with dynamic question formats</p>
                </CardContent>
              </Card>
              
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 group">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <TrendingUp className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold font-display">Real-Time Feedback</h3>
                  <p className="text-muted-foreground">Instant results keep learners motivated and deeply engaged</p>
                </CardContent>
              </Card>
              
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 group">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold font-display">Foster Collaboration</h3>
                  <p className="text-muted-foreground">Shared learning and friendly competition that builds confidence</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Customization Section */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <Palette className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Make It Yours</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold font-display">
                  Customization &{" "}
                  <span className="text-gradient">Personalization</span>
                </h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  Every audience is unique, so your quizzes should be too! Tailor everything — from question types to branding — to match your goals and reflect your style.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Whether you're aiming for site-wide engagement in education or crafting a one-of-a-kind learning experience, our flexible platform adapts to your needs and preferences, ensuring that each session feels personal and purposeful.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: CheckCircle, label: "Custom Question Types" },
                  { icon: Palette, label: "Brand Your Quizzes" },
                  { icon: TrendingUp, label: "Flexible Scoring" },
                  { icon: Users, label: "Team Collaboration" },
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 text-center"
                  >
                    <item.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                    <p className="font-semibold text-foreground">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Use Cases for{" "}
                <span className="text-gradient">Quizzes</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Quizzes aren't just for classrooms! Use them to energize presentations, foster teamwork, or add fun to gatherings.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: GraduationCap, title: "Education", desc: "Teaching and training sessions" },
                { icon: Presentation, title: "Presentations", desc: "Energize your audience" },
                { icon: Briefcase, title: "Workplace", desc: "Foster teamwork and training" },
                { icon: PartyPopper, title: "Social Events", desc: "Fun icebreakers and games" },
              ].map((item, index) => (
                <Card key={index} className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 group">
                  <CardContent className="p-6 text-center space-y-3">
                    <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold font-display">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-4xl relative z-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm mb-6">
              <Heart className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Trusted Worldwide</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
              User Trust &{" "}
              <span className="text-gradient">Adoption</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
              Millions of learners, educators, and businesses worldwide trust our quiz platform. Renowned institutions and organizations rely on our versatile, customizable solutions to deliver the ultimate learning experience.
            </p>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              When you choose our quizzes, you join a community committed to interactive education, engagement, and success.
            </p>
          </div>
        </section>

        {/* Solutions & Plans Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-muted" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Quiz Solutions &{" "}
                <span className="text-gradient">Plans</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                We offer a range of plans to suit every need, including a robust free tier for starters and feature-rich options for power users. Find solutions comparable to popular platforms like Kahoot, with added flexibility for your specific requirements.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all group">
                <CardContent className="p-8 text-center space-y-4">
                  <h3 className="text-2xl font-bold font-display">Free Tier</h3>
                  <p className="text-muted-foreground">Perfect for getting started with essential quiz features</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 backdrop-blur-sm border-primary/50 hover:border-primary transition-all group scale-105">
                <CardContent className="p-8 text-center space-y-4">
                  <h3 className="text-2xl font-bold font-display text-gradient">Premium</h3>
                  <p className="text-muted-foreground">Advanced reporting, multimedia integration & more</p>
                </CardContent>
              </Card>
              
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all group">
                <CardContent className="p-8 text-center space-y-4">
                  <h3 className="text-2xl font-bold font-display">Enterprise</h3>
                  <p className="text-muted-foreground">Larger participant numbers & custom solutions</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Quiz Topics Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Popular Quiz{" "}
                <span className="text-gradient">Topics</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Explore fun and educational quizzes across different categories
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {quizTopicPages.map((page) => (
                <Link
                  key={page.path}
                  to={page.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className="group p-5 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg hover:scale-[1.02]"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                    {page.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">{page.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CSW vs Comparison Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-muted" />
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                CSW vs{" "}
                <span className="text-gradient">Alternatives</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                See how CSW compares to other popular quiz platforms
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {comparisonPages.map((page) => (
                <Link
                  key={page.path}
                  to={page.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className="group p-5 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg hover:scale-[1.02]"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      CSW vs {page.name}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <p className="text-sm text-muted-foreground">{page.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Start Testing Your{" "}
              <span className="text-gradient">Knowledge</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Join students using CSW's quizzes to ace their exams.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Create Your First Quiz
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Quizzes;